import SectionHeader from "../../../../util/component/SectionHeader";
import { Stack, Typography } from "@mui/material";
import { isEqual } from "lodash";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import {FormProvider, useForm, useWatch} from "react-hook-form";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { yupResolver } from "@hookform/resolvers/yup";
import { useRecoilState, useSetRecoilState } from "recoil";
import { ICarGroup, InvoiceType, IBooking, IProduct, ReactQueryKey, RentalType, VehicleType, RouteParams, DEFAULT_VALUE_PA_ID_NUMBER, MOBILITY_STORE_ALL } from "../../types";
import CustomLoader from "../../../../util/component/CustomLoader";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { setSnackbar } from "../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../util/state/globalVariables";
import {
    AncillarySubtype,
    getBookingByIdWithMeta,
    getCarGroupsByLocation,
    getCompany,
    getCoupon,
    getMobilePrefixes,
    getProduct,
    getProductLeisure,
    getRatePlan,
    IGetCompanyReturn,
    IGetProductReturn,
    IRangeDate,
    RESPONSE_TYPE_SERVICE
} from "../../api/services";
import { dayjsDateTime, getCarGroupCode, LOCALE_DATE_TIME_FORMAT } from "../../utils";
import EditSections from "./_components/EditSections";
import StateManager from "./_components/StateManager";
import {
    editBusinessAgreementCheckValidate,
    editBusinessAgreementCig,
    editMandatoryDossierIsRequired,
    editRangeDateValidity,
    editRatePlanOfferIDState,
    editPendingBookingRce,
    idSmProductDefaultByEditState,
    originalDiscountOverprice
} from "./recoil";
import { IFormValues } from "./types";
import TableAvailabilities from "./_components/sections/table-availabilities/TableAvailabilities";
import { validationSchema } from "./form";

/**
 * Edit booking page
 */
const EditBooking = () => {
    const { t , i18n} = useTranslation('booking');

    const { dispatchGlobalData, globalData } = useContext(GlobalContext);

    const { id } = useParams<RouteParams>();

    const queryClient = useQueryClient();

    const [loading, setLoading] = useState<boolean>(true);

    const [bACheckValidate, setBusinessAgreementCheckValidate] = useRecoilState<boolean | undefined>(editBusinessAgreementCheckValidate);
    const [hasCig, setBusinessAgreementHasCig] = useRecoilState<boolean | undefined>(editBusinessAgreementCig);
    const [mandatoryDossierRequired, seMandatoryDossierIsRequired] = useRecoilState<boolean | undefined>(editMandatoryDossierIsRequired);
    const [rangeDate, setRangeDate] = useRecoilState<IRangeDate | undefined>(editRangeDateValidity);
    const setIdSmProductDefault = useSetRecoilState(idSmProductDefaultByEditState);
    const [discountOverprice, setOriginalDiscountOverprice] = useRecoilState<number | null>(originalDiscountOverprice);

    const [editRatePlanOfferID, setEditRatePlanOfferID] = useRecoilState(editRatePlanOfferIDState);

    const [isPendingBookingRce, setIsPendingBookingRce] = useRecoilState(editPendingBookingRce);

    const form = useForm<IFormValues>({
        defaultValues: {
            basicDetails: {
                rentalType: RentalType.RETAIL,
                pickupLocation: null,
                pickupLocationTimezone: null,
                dropoffLocation: null,
                pickupDate: dayjsDateTime()
                    .add(1, 'day')
                    .set('h', 10)
                    .set('m', 0),
                dropoffDate: dayjsDateTime()
                    .add(4, 'day')
                    .set('h', 10)
                    .set('m', 0),
                vehicleCategory: VehicleType.CAR,
                carGroup: null,
                lockGroup: false,
                mileageIncluded: null,
                customerCode: '',
                cig: '',
                dossierNumber: '',
                tenant: null,
                dealer: null
            },
            extraServices: {
                ancillary: {},
                discountCode: '',
                discountOverprice: null,
                note: '',
                affiliationCode: '',
                flightNumber: '',
                discountType: null,
/*                deliveryState: '',
                collectionState: ''*/
            },
            firstDriver: {
                id: "",
                name: "",
                surname: "",
                birthDate: null,
                birthPlace: "",
                birthNation: "",
                residenceCountry: "",
                fiscalCode: "",
                drivingLicenseNumber: "",
                email: "",
                mobile: "",
                uuid: "",
                mobilePrefix: null,
                gender: ''
            },
            invoice: {
                id: "",
                type: InvoiceType.PERSON,
                name: "",
                surname: "",
                birthDate: null,
                birthNation: "",
                birthPlace: "",
                residenceCountry: "",
                fiscalCode: "",
                drivingLicenseNumber: "",
                email: "",
                mobile: "",
                billingCountry: null,
                billingCity: "",
                billingStreetName: "",
                billingStreetNumber: "",
                billingPostalCode: "",
                billingStreetNameGoogleSearch: "",
                certifiedEmail: "",
                vatNumber: "",
                vatNumberCountry: "",
                invoiceCode: "",
                companyName: "",
                uuid: "",
                mobilePrefix: null,
                billingProvince: null,
                linkedToBusinessAgreement: false,
                legalEntity: null,
                publicAuthority: false,
                paIdNumber: ''
            },
            bookingOwner: {
                id: "",
                name: "",
                surname: "",
                birthDate: null,
                birthNation: "",
                birthPlace: "",
                residenceCountry: "",
                fiscalCode: "",
                drivingLicenseNumber: "",
                email: "",
                mobile: "",
                companyName: "",
                country: "",
                city: "",
                streetName: "",
                streetNumber: "",
                postalCode: "",
                certifiedEmail: "",
                telephone: "",
                invoiceCode: "",
                vatNumber: "",
                vatNumberCountry: "",
                mobilePrefix: null,
                linkedToBusinessAgreement: false,
                legalEntity: null,
                publicAuthority: false,
                paIdNumber: ''
            },
            additionalDrivers: [],
            forceBookingOwner: true,
        },
        reValidateMode: 'onSubmit',
        resolver: yupResolver(validationSchema, {}, { rawValues: true }),
        context: {
            mandatoryDossierRequired,
            hasCig,
            rangeDate,
            bACheckValidate,
            dmsAssociatedOperator: globalData.user.mobilityStores.map(
                ((elem:any) => {
                    return elem.sapId;
                })
            ),
            originalDiscountOverprice: discountOverprice
        }
    })

    const [tenant, setTenant] = useState<string>("");
    const [dealer, setDealer] = useState<string>("");
    //const tenant = useWatch({ control: form.control, name: "basicDetails.tenant" });
    //const dealer = useWatch({ control: form.control, name: "basicDetails.dealer" });


    const bookingQuery = useQuery({
        queryKey: [ReactQueryKey.BookingById, id],
        queryFn: () => {
            if (id) {
                return getBookingByIdWithMeta(id, i18n.language);
            }
        },
        cacheTime: 0,
        refetchOnMount: true,
        onSuccess: async (data) => {
            try {
                const booking = data?.booking;
                if (!booking) {
                    return;
                }
                setIsPendingBookingRce(booking.rce && booking.status == "PENDING");

                // set idSmProduct
                setIdSmProductDefault(booking.idSmProduct);
                // set originalDiscountOverprice
                setOriginalDiscountOverprice(booking.discountOverprice ?? null);

                const productParams = {
                    segment: booking.rentalType,
                    tenant: booking.tenant, // tenant as string,
                    dealer: booking.dealer //dealer as string
                }

                let product: IGetProductReturn | IGetCompanyReturn;
                if (booking.rentalType === RentalType.RETAIL) {
                    product = await queryClient.fetchQuery({
                        queryKey: [ReactQueryKey.Product, productParams],
                        queryFn: () => getProduct({businessContinuityInsToolMode: false, ...productParams})
                    })

                    if(booking.codeDescription == RESPONSE_TYPE_SERVICE.leisure && !!booking.discountCode) {
                        const couponResponse = await queryClient.fetchQuery({
                            queryKey: [ReactQueryKey.Coupon, {tenant: tenant as string, dealer: dealer as string}],
                            queryFn: () => getCoupon({
                                pickUpLocation: '',
                                dropOffLocation: '',
                                pickUpDate: booking.pickupDate,
                                dropoffDate: booking.dropoffDate,
                                promoCode: booking.discountCode!,
                                carGroupCode: undefined,
                                productCodes: product.products.map(p => p.code),
                                tenant: booking.tenant, // tenant as string,
                                dealer: booking.dealer //dealer as string
                            })
                        });
                        const productList = couponResponse?.leisureResult?.productList;
                        
                        if(!!productList) {
                            product = await queryClient.fetchQuery({
                                queryKey: [ReactQueryKey.Product, productParams],
                                queryFn: () => getProductLeisure({
                                    leisureCode: booking.discountCode!,
                                    productList ,
                                    pickUpDate: booking.pickupDate,
                                    tenant: booking.tenant, // tenant as string,
                                    dealer: booking.dealer //dealer as string
                                })
                            });
                        }

                    }

                } else {
                    const customerCode = booking?.customerCode as string;
                    const pickupDate = booking?.pickupDate
                    product = await queryClient.fetchQuery({
                        queryKey: [ReactQueryKey.Product, productParams],
                        queryFn: () => getCompany(customerCode, pickupDate, booking.tenant, booking.dealer)
                    });
                    // set state for corporate form
                    if (!!customerCode) {
                        setBusinessAgreementCheckValidate(!!product);
                        setBusinessAgreementHasCig(!!(product as IGetCompanyReturn)?.cig && (product as IGetCompanyReturn)?.cig?.length !== 0);
                        seMandatoryDossierIsRequired((product as IGetCompanyReturn)?.mandatoryDossier);
                        setRangeDate(!!(product as IGetCompanyReturn)?.rangeDate ? (product as IGetCompanyReturn)?.rangeDate : {
                            startDate: null,
                            endDate: null
                        });
                    } else {
                        setBusinessAgreementCheckValidate(false);
                        setBusinessAgreementHasCig(false);
                        seMandatoryDossierIsRequired(false);
                        setRangeDate({startDate: null, endDate: null});
                    }
                }

                const pickupLocation = product.locations
                    .find(v => v.id === booking.pickupLocation.id) || null;
                const dropoffLocation = product.locations
                    .find(v => v.id === booking.dropoffLocation.id) || null;

                // get car group
                const carGroupsParams = {
                    pickupLocation: booking.pickupLocation.id,
                    dropoffLocation: booking.dropoffLocation.id,
                    requestDate: dayjs(booking.bookingDatetime).toISOString(),
                    pickupDate: dayjs(booking.pickupDate).format(LOCALE_DATE_TIME_FORMAT),
                    dropoffDate: dayjs(booking.dropoffDate).format(LOCALE_DATE_TIME_FORMAT),
                    products: product.products as IProduct[],
                    tenant: booking.tenant, // tenant as string,
                    dealer: booking.dealer //dealer as string
                }

                const carGroups = await queryClient.fetchQuery({
                    queryKey: [ReactQueryKey.CarGroupsByLocation, carGroupsParams],
                    queryFn: () => getCarGroupsByLocation(carGroupsParams)
                });


                const carGroup = carGroups?.carGroups
                    .find(v =>
                        String(v.acrissCode) === String(booking.carGroup.acrissCode) &&
                        (v.sippCode ?? null) === (booking.carGroup.sippCode ?? null)
                    ) || null;

                const dmsAssociatedOperator = globalData?.user?.mobilityStores.map(
                    ((elem:any) => {
                        return elem.sapId;
                    })
                );

                const idProfile = dmsAssociatedOperator?.includes(MOBILITY_STORE_ALL) || (!!pickupLocation && dmsAssociatedOperator?.includes(pickupLocation.id)) ? globalData?.user?.profiles
                    .map((p: any) => (
                        p.id
                    )) : [];

                const ratePlan = await queryClient.fetchQuery({
                    queryKey: [ReactQueryKey.RatePlan, {tenant: tenant as string, dealer: dealer as string}],
                    queryFn: () => getRatePlan({
                        pickupDate: dayjs(booking.pickupDate).format(LOCALE_DATE_TIME_FORMAT),
                        dropoffDate: dayjs(booking.dropoffDate).format(LOCALE_DATE_TIME_FORMAT),
                        pickupLocation: pickupLocation?.id as string,
                        dropoffLocation: dropoffLocation?.id as string,
                        carGroupCode: getCarGroupCode(carGroup as ICarGroup),
                        products: product.products as IProduct[],
                        offerId: booking.offerId ?? '',
                        firstEdit: true,
                        businessAgreement: booking.customerCode ?? null,
                        profileIdList: idProfile,
                        requestDate: dayjs(booking.bookingDatetime).toISOString() || undefined,
                        tenant: booking.tenant,  //tenant as string,
                        dealer: booking.dealer //dealer as string
                    })
                });

                if(!!ratePlan.offerId && editRatePlanOfferID != ratePlan.offerId)
                    setEditRatePlanOfferID(ratePlan.offerId);

                // set ancillaries
                let ancillary: any = {};
                for (let count = 0; count < booking.ancillaries.length; count++) {
                    if (booking.ancillaries[count].subtype === AncillarySubtype.AdditionalDrivers) {
                        ancillary[AncillarySubtype.AdditionalDrivers] = booking.ancillaries[count].units;
                    } else {
                        const defaultObj = ratePlan.ancillaries.filter((elem: any) => elem.ancillaryCode === booking.ancillaries[count].ancillaryCode)[0];

                        if (defaultObj.maxUnits > 1) {
                            ancillary[booking.ancillaries[count].ancillaryCode] = booking.ancillaries[count].units;
                        } else if (defaultObj.mileageNeeded) {
                            ancillary[booking.ancillaries[count].ancillaryCode] = booking.ancillaries[count].mileage;
                        } else {
                            ancillary[booking.ancillaries[count].ancillaryCode] = booking.ancillaries[count].selected;
                        }
                    }
                }
                let additionalDrivers = [];

                // set additional drivers form
                const additionalDriversAncillary = ancillary[AncillarySubtype.AdditionalDrivers] as number | undefined;
                if (additionalDriversAncillary) {
                    if (booking?.additionalDrivers?.length !== additionalDriversAncillary) {
                        for (let i = 0; i < additionalDriversAncillary; i++) {
                            additionalDrivers.push({
                                id: '',
                                name: '',
                                surname: '',
                                email: ''
                            })
                        }
                    } else {
                        additionalDrivers = booking?.additionalDrivers.map((elem: any) => {
                            elem.id = elem.customerId;
                            return elem;
                        });
                    }
                }

                // set prefix mobile number
                const mobilePrefixes = await queryClient.fetchQuery({
                    queryKey: [ReactQueryKey.MobilePrefixes],
                    queryFn: () => getMobilePrefixes()
                });
                const foundMobilePrefixFirstDriver = mobilePrefixes?.find(mp => isEqual(mp?.prefix, booking?.firstDriver?.mobilePrefix?.prefix));
                const foundMobilePrefixInvoice = mobilePrefixes?.find(mp => isEqual(mp?.prefix, booking?.invoicePerson?.mobilePrefix?.prefix));
                const foundMobilePrefixBookingOwner = mobilePrefixes?.find(mp => isEqual(mp?.prefix, booking?.bookingOwner?.mobilePrefix?.prefix ?? booking?.bookingCompanyOwner?.mobilePrefix?.prefix));

                form.reset({
                    basicDetails: {
                        rentalType: booking.rentalType,
                        pickupLocation: pickupLocation,
                        dropoffLocation: dropoffLocation,
                        pickupDate: !!booking.pickupDate ? dayjs(booking.pickupDate) : '',
                        dropoffDate: !!booking.dropoffDate ? dayjs(booking.dropoffDate) : '',
                        vehicleCategory: booking.carGroup.bookedVehicleType,
                        carGroup: carGroup,
                        lockGroup: false,
                        mileageIncluded: ratePlan.mileageList.find(m => m.value === booking.mileageIncluded) ? booking.mileageIncluded : null,
                        customerCode: booking.customerCode,
                        cig: booking.cig,
                        dossierNumber: booking.dossierNumber,
                        tenant: booking.tenant,
                        dealer: booking.dealer
                    },
                    extraServices: {
                        ancillary: ancillary,
                        discountCode: booking.discountCode ?? undefined,
                        discountOverprice: booking.discountOverprice ?? null,
                        fcaBankCardNumber: booking.fcaBankCardNumber ?? undefined,
                        note: '',
                        affiliationCode: booking.affiliationCode ?? '',
                        flightNumber: booking.flightNumber ?? '',
                        discountType: booking.discountCode ? 'promo' : booking.fcaBankCardNumber ? 'fca' : null,
                        deliveryState: booking.deliveryState ?? undefined,
                        collectionState: booking.collectionState ?? undefined,
                        collectionStreetName: booking.collectionStreetName ?? undefined,
                        collectionStreetNumber: booking.collectionStreetNumber ?? undefined,
                        collectionPostalCode: booking.collectionPostalCode ?? undefined,
                        collectionCity: booking.collectionCity ?? undefined,
                        deliveryStreetName: booking.deliveryStreetName ?? undefined,
                        deliveryStreetNumber: booking.deliveryStreetNumber ?? undefined,
                        deliveryPostalCode: booking.deliveryPostalCode ?? undefined,
                        deliveryCity: booking.deliveryCity ?? undefined
                    },
                    bookingOwner: {
                        id: booking.bookingOwner?.customerId ? booking.bookingOwner.customerId : booking.bookingCompanyOwner?.customerId ? booking.bookingCompanyOwner.customerId : '',
                        name: booking.bookingOwner?.name ?? '',
                        surname: booking.bookingOwner?.surname ?? '',
                        birthDate: booking.bookingOwner?.birthDate ? dayjs(booking.bookingOwner.birthDate).format('DD-MM-YYYY') : '',
                        birthNation: booking.bookingOwner?.birthNation ?? '',
                        birthPlace: booking.bookingOwner?.birthPlace ?? '',
                        residenceCountry: booking.bookingOwner?.residenceCountry ?? '',
                        fiscalCode: booking.bookingOwner?.fiscalCode ?? '',
                        drivingLicenseNumber: booking.bookingOwner?.drivingLicenseNumber ?? '',
                        email: booking.bookingOwner?.email ? booking.bookingOwner.email : booking.bookingCompanyOwner?.email ? booking.bookingCompanyOwner.email : '',
                        mobile: booking.bookingOwner?.mobile ? booking.bookingOwner.mobile : booking.bookingCompanyOwner?.mobile ? booking.bookingCompanyOwner.mobile : '',
                        mobilePrefix: foundMobilePrefixBookingOwner ?? null,
                        companyName: booking.bookingCompanyOwner?.companyName ?? '',
                        country: booking.bookingCompanyOwner?.country ?? '',
                        city: booking.bookingCompanyOwner?.city ?? '',
                        streetName: booking.bookingCompanyOwner?.streetName ?? '',
                        streetNumber: booking.bookingCompanyOwner?.streetNumber ?? '',
                        postalCode: booking.bookingCompanyOwner?.postalCode ?? '',
                        certifiedEmail: booking.bookingCompanyOwner?.pec ?? '',
                        telephone: booking.bookingCompanyOwner?.telephone ?? '',
                        invoiceCode: booking.bookingCompanyOwner?.sdiCode ?? '',
                        vatNumber: booking.bookingCompanyOwner?.vatNumber ?? '',
                        vatNumberCountry: booking.bookingCompanyOwner?.vatNumberCountry ?? '',
                        siren: booking.bookingCompanyOwner?.siren ?? '',
                        siret: booking.bookingCompanyOwner?.siret ?? '',
                        legalEntity: booking.bookingCompanyOwner?.legalEntity ?? null,
                        publicAuthority: booking.bookingCompanyOwner?.publicAuthority === '1',
                        paIdNumber: booking.bookingCompanyOwner?.publicAuthority === '1' ? (!!booking.bookingCompanyOwner?.paIdNumber ?  booking.bookingCompanyOwner?.paIdNumber : DEFAULT_VALUE_PA_ID_NUMBER) : '',
                    },
                    invoice: {
                        id: booking.invoicePerson?.customerId ? booking.invoicePerson?.customerId : booking.invoiceCompany?.customerId ? booking.invoiceCompany?.customerId : '',
                        type: booking.invoicePerson?.type ? booking.invoicePerson.type : booking.invoiceCompany?.type ? booking.invoiceCompany.type : '',
                        name: booking.invoicePerson?.name ?? '',
                        surname: booking.invoicePerson?.surname ?? '',
                        birthDate: booking.invoicePerson?.birthDate ? dayjs(booking.invoicePerson.birthDate) : null,
                        birthNation: booking.invoicePerson?.birthNation ?? '',
                        birthPlace: booking.invoicePerson?.birthPlace ?? '',
                        residenceCountry: booking.invoicePerson?.residenceCountry ?? '',
                        fiscalCode: booking.invoicePerson?.fiscalCode ? booking.invoicePerson.fiscalCode : booking.invoiceCompany?.fiscalCode ? booking.invoiceCompany.fiscalCode : '',
                        drivingLicenseNumber: booking.invoicePerson?.drivingLicenseNumber ?? '',
                        email: booking.invoicePerson?.email ? booking.invoicePerson.email : booking.invoiceCompany?.email ? booking.invoiceCompany.email : '',
                        mobile: booking.invoicePerson?.mobile ?? '',
                        billingCountry: booking.invoicePerson?.billingCountry ? booking.invoicePerson.billingCountry : booking.invoiceCompany?.billingCountry ? booking.invoiceCompany.billingCountry : '',
                        billingCity: booking.invoicePerson?.billingCity ? booking.invoicePerson.billingCity : booking.invoiceCompany?.billingCity ? booking.invoiceCompany.billingCity : '',
                        billingStreetName: booking.invoicePerson?.billingStreetName ? booking.invoicePerson.billingStreetName : booking.invoiceCompany?.billingStreetName ? booking.invoiceCompany.billingStreetName : '',
                        billingStreetNumber: booking.invoicePerson?.billingStreetNumber ? booking.invoicePerson.billingStreetNumber : booking.invoiceCompany?.billingStreetNumber ? booking.invoiceCompany.billingStreetNumber : '',
                        billingPostalCode: booking.invoicePerson?.billingPostalCode ? booking.invoicePerson.billingPostalCode : booking.invoiceCompany?.billingPostalCode ? booking.invoiceCompany.billingPostalCode : '',
                        billingProvince: booking.invoicePerson?.billingProvince ? booking.invoicePerson.billingProvince : booking.invoiceCompany?.billingProvince ? booking.invoiceCompany.billingProvince : null,
                        certifiedEmail: booking.invoicePerson?.pec ? booking.invoicePerson.pec : booking.invoiceCompany?.pec ? booking.invoiceCompany.pec : '',
                        vatNumber: booking.invoiceCompany?.vatNumber ? booking.invoiceCompany.vatNumber : '',
                        vatNumberCountry: booking.invoiceCompany?.vatNumberCountry ? booking.invoiceCompany.vatNumberCountry : '',
                        siren: booking.invoiceCompany?.siren ?? '',
                        siret: booking.invoiceCompany?.siret ?? '',
                        invoiceCode: booking.invoiceCompany?.invoiceCode ? booking.invoiceCompany.invoiceCode : '',
                        companyName: booking.invoiceCompany?.companyName ?? '',
                        uuid: '',
                        mobilePrefix: foundMobilePrefixInvoice ?? null,
                        linkedToBusinessAgreement: true,
                        legalEntity: booking.invoiceCompany?.legalEntity ? booking.invoiceCompany?.legalEntity : null,
                        publicAuthority: booking.invoiceCompany?.publicAuthority === '1',
                        paIdNumber: booking.invoiceCompany?.publicAuthority === '1' ? (!!booking.invoiceCompany?.paIdNumber ?  booking.invoiceCompany?.paIdNumber : DEFAULT_VALUE_PA_ID_NUMBER) : ''
                    },
                    firstDriver: {
                        //uuid: booking.firstDriver?.uuid ?? '',
                        id: booking.firstDriver?.customerId ?? '',
                        name: booking.firstDriver?.name ?? '',
                        surname: booking.firstDriver?.surname ?? '',
                        birthDate: booking.firstDriver?.birthDate ? dayjs(booking.firstDriver.birthDate) : null,
                        birthNation: booking.firstDriver?.birthNation ?? '',
                        birthPlace: booking.firstDriver?.birthPlace ?? '',
                        residenceCountry: booking.firstDriver?.residenceCountry ?? '',
                        email: booking.firstDriver?.email ?? '',
                        mobile: booking.firstDriver?.mobile ?? '',
                        fiscalCode: booking.firstDriver?.fiscalCode ?? '',
                        drivingLicenseNumber: booking.firstDriver?.drivingLicenseNumber ?? '',
                        gender: booking.firstDriver?.gender ?? '',
                        mobilePrefix: foundMobilePrefixFirstDriver ?? null,
                    },
                    additionalDrivers: additionalDrivers,
                    forceBookingOwner: true,
                })

                setLoading(false);
            } catch(error) {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
                setLoading(false);
            }
        },
        onError: () => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.error.default'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
            setLoading(false);
        }
    });

    useEffect(() => {
        if (bookingQuery.data?.booking) {
            const booking = bookingQuery.data.booking; // Ensure booking is defined
            // tenant
            setTenant(booking.tenant);
            // dealer
            setDealer(booking.dealer);
        }
    }, [bookingQuery.data, setTenant, setDealer]);

    if (loading) {
        return (
             <CustomLoader loading />
        )
    }

    return (
        <>
            <SectionHeader title={t('label.updateBooking')} subtitle={
                <Stack direction="row" alignItems="center" columnGap={3}>
                    <Stack direction="row" alignItems="end" columnGap={1}>
                        <Typography variant="bodyLarge">ID:</Typography>
                        <Typography variant="bodyLarge">{id}</Typography>
                    </Stack>
                </Stack>
            }/>

            <FormProvider {...form}>
                <Stack rowGap={2} p={3}>
                    <EditSections bookingId={id}/>
                    <TableAvailabilities />
                </Stack>
                <StateManager />

            </FormProvider>
        </>
    )
}


export default EditBooking;
